import {
  InsureIQApiClient,
  InsureIQApiClientAuthMethods,
} from '@harvestiq/insureiq-api-client';
import { createContext, PropsWithChildren, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Loading from '../components/Loading/Loading';

type InsureIQApiClientContextProps = {
  insureIQApiClient: InsureIQApiClient;
};

export const InsureIQApiClientContext =
  createContext<InsureIQApiClientContextProps | null>(null);

export function InsureIQApiClientProvider({ children }: PropsWithChildren) {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const authMethod = InsureIQApiClientAuthMethods.auth0AccessToken;

  const getAuth0AccessToken = () => {
    // Only attempt to get the access token if the user is authenticated
    if (isAuthenticated) {
      return getAccessTokenSilently();
    }
    // Otherwise make API calls without an access token
    return Promise.resolve(undefined);
  };

  const insureIQApiClient = new InsureIQApiClient({
    authMethod,
    getAuth0AccessToken,
  });

  const contextValue: InsureIQApiClientContextProps = {
    insureIQApiClient,
  };

  return (
    <InsureIQApiClientContext.Provider value={contextValue}>
      {isLoading ? <Loading /> : children}
    </InsureIQApiClientContext.Provider>
  );
}

export function useInsureIQApiClient() {
  const context = useContext(InsureIQApiClientContext);
  if (!context) {
    throw new Error(
      'useInsureIQApiClient must be used within an InsureIQApiClientProvider'
    );
  }
  return context;
}
